/**
 * usePickSlate Hook - Phase 2D-2
 * 
 * Fetches markets/outcomes for Pick Mode.
 */
import { useState, useEffect, useCallback } from 'react';
import { PickGMService } from '../api';
import type { PickSlateResponse, PickSlateEvent } from '../types';

interface UsePickSlateResult {
  slate: PickSlateResponse | null;
  events: PickSlateEvent[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePickSlate(poolId: string): UsePickSlateResult {
  const [slate, setSlate] = useState<PickSlateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSlate = useCallback(async () => {
    if (!poolId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await PickGMService.getPickSlate(poolId);
      setSlate(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pick slate');
    } finally {
      setLoading(false);
    }
  }, [poolId]);

  useEffect(() => {
    fetchSlate();
  }, [fetchSlate]);

  return {
    slate,
    events: slate?.events || [],
    loading,
    error,
    refresh: fetchSlate,
  };
}
